import type { App } from 'vue';
import axios from 'axios';

export interface ErrorContext {
  component?: string;
  action?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Get user-friendly error message from error object
 */
export function getUserFriendlyMessage(error: unknown): string {
  // Handle axios errors
  if (axios.isAxiosError(error)) {
    if (!error.response) {
      return 'Unable to connect to the server. Please check your internet connection.';
    }

    const status = error.response.status;
    
    switch (status) {
      case 400:
        return 'Invalid request. Please check your input and try again.';
      case 401:
        return 'Your session has expired. Please log in again.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 408:
        return 'Request timeout. Please try again.';
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      case 500:
        return 'Server error. Please try again later.';
      case 502:
      case 503:
      case 504:
        return 'Service temporarily unavailable. Please try again later.';
      default:
        return `An error occurred (${status}). Please try again.`;
    }
  }

  // Handle Error objects
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    
    // Network errors
    if (message.includes('network') || message.includes('fetch failed')) {
      return 'Network error. Please check your connection and try again.';
    }
    
    // Timeout errors
    if (message.includes('timeout')) {
      return 'Request timed out. Please try again.';
    }
    
    // Return the error message if it's user-friendly
    if (error.message.length < 100 && !message.includes('undefined')) {
      return error.message;
    }
  }

  // Default message
  return 'An unexpected error occurred. Please try again.';
}

/**
 * Log error with context for debugging
 */
export function logError(error: unknown, context?: ErrorContext): void {
  const errorInfo = {
    timestamp: new Date().toISOString(),
    error: error instanceof Error ? {
      name: error.name,
      message: error.message,
      stack: error.stack,
    } : error,
    context,
    userAgent: navigator.userAgent,
    url: window.location.href,
  };

  // Log to console in development
  if (import.meta.env.DEV) {
    console.error('Application Error:', errorInfo);
  } else {
    // In production, you might want to send to an error tracking service
    console.error('Error:', errorInfo.error);
  }

  // TODO: Send to error tracking service (e.g., Sentry, LogRocket)
  // sendToErrorTracking(errorInfo);
}

/**
 * Setup global error handler for Vue app
 */
export function setupGlobalErrorHandler(app: App): void {
  // Handle Vue errors
  app.config.errorHandler = (err, instance, info) => {
    logError(err, {
      component: instance?.$options.name || instance?.$options.__name,
      action: info,
    });

    // Don't throw in production to prevent app crash
    if (import.meta.env.DEV) {
      console.error('Vue Error:', err);
    }
  };

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    logError(event.reason, {
      action: 'unhandledRejection',
    });

    // Prevent default browser error handling
    event.preventDefault();
  });

  // Handle global errors
  window.addEventListener('error', (event) => {
    logError(event.error, {
      action: 'globalError',
      metadata: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });
}

/**
 * Check if error is a network error
 */
export function isNetworkError(error: unknown): boolean {
  if (axios.isAxiosError(error)) {
    return !error.response;
  }
  
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return message.includes('network') || message.includes('fetch failed');
  }
  
  return false;
}

/**
 * Check if error is an authentication error
 */
export function isAuthError(error: unknown): boolean {
  if (axios.isAxiosError(error)) {
    return error.response?.status === 401 || error.response?.status === 403;
  }
  
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return message.includes('auth') || message.includes('unauthorized');
  }
  
  return false;
}

/**
 * Check if error is a validation error
 */
export function isValidationError(error: unknown): boolean {
  if (axios.isAxiosError(error)) {
    return error.response?.status === 400 || error.response?.status === 422;
  }
  
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return message.includes('validation') || message.includes('invalid');
  }
  
  return false;
}
